/**
 * درج الجرس وتفضيلات الإشعارات وربط تليجرام.
 * المسارات: `/notifications/*` (٧ مسارات) — عقدها في `app/routers/notifications.py`.
 *
 * 🔒 قاعدة مقفولة في العقد نفسه: **الإشعار مفيهوش أفعال.** الرد ما بيرجّعش
 *    أي مصفوفة `actions`، والملف ده ما بيخترعش واحدة. الضغط على الإشعار
 *    بيوديك للمهمة نفسها في «مكتبي» — عشان ما يبقاش فيه طابور مهام تاني.
 *
 * ⚠️ ممنوع «نجاح» على رد فاضي: كل دالة هنا بتتأكد إن الجسم اللي رجع فيه
 *    فعلًا اللي المفروض يكون فيه، وبترمي خطأ صايح لو لأ.
 */
import { ApiError, api } from "./client";
import { HOME_PATH, ROUTES } from "@/lib/router";
import type { ListMeta } from "./types";

/* ══════════════════════════════════════════════════════════════════
   الأنواع
   ══════════════════════════════════════════════════════════════════ */

/** الشريحة: «يخصني» ولا «فريقي». غياب القيمة = «الكل». */
export type NotificationScope = "mine" | "team";

/** الشريحة المعروضة في الشاشة — «الكل» مش قيمة في السيرفر، هي غياب الفلتر */
export type NotificationTab = "all" | NotificationScope;

/** وجهة الإشعار: شاشة + بارامترات. بتتحوّل لمسار بـ`deepLinkPath`. */
export type DeepLink = {
  screen?: string | null;
  params?: Record<string, string | number | null> | null;
};

export type NotificationRow = {
  id: string;
  /** handoff · sla_warning · event · decision_needed · system … */
  type: string;
  urgency: string;
  title_ar: string;
  body_ar: string | null;
  origin_dept: string | null;
  origin_label_ar: string | null;
  deep_link: DeepLink | null;
  scope: NotificationScope;
  /** عدد المرات اللي الحدث اتكرر فيها جوّه نفس الإشعار */
  rollup_count: number;
  first_occurred_at: string | null;
  last_occurred_at: string | null;
  /** `null` = لسه ما اتقراش */
  read_at: string | null;
};

export type NotificationCounts = { all: number; mine: number; team: number };

export type NotificationsPage = {
  rows: NotificationRow[];
  meta: ListMeta | null;
  counts: NotificationCounts | null;
};

export type NotificationPrefs = {
  handoff: boolean;
  sla_warning: boolean;
  announcements: boolean;
  daily_digest: boolean;
  telegram_linked: boolean;
  telegram_muted_until: string | null;
  digest_at_local: string | null;
  /** نص جاي من السيستم بيشرح حدود التفضيلات — بيتعرض زي ما هو */
  notice_ar: string | null;
};

/** المفاتيح اللي الموظف بيقلّبها في الشاشة */
export type PrefToggleKey = "handoff" | "sla_warning" | "announcements" | "daily_digest";

export type TelegramLink = {
  /** رابط بوت الأكاديمية — لاتيني صافي */
  deep_link: string;
  /** كود مكتوب بديل لو الرابط ما فتحش */
  manual_code: string;
  expires_at: string | null;
};

/* ══════════════════════════════════════════════════════════════════
   حارس الشكل — «رد فاضي مش نجاح»
   ══════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ ده مش تدقيق شكلي زيادة. عطل حقيقي حصل قبل كده: مسار رجّع `null`
 *    والواجهة فسّرته نجاح، فالموظف فضل شايف حالة قديمة وهو فاكرها اتحدّثت.
 */
function shapeError(path: string, got: unknown): ApiError {
  console.error(`[notifications] رد مش متوقّع من ${path}`, got);
  return new ApiError(
    502,
    {
      code: "BAD_RESPONSE_SHAPE",
      message_ar: "الرد اللي جه من السيستم مش مفهوم — جرّب تاني أو بلّغ إدارة الأكاديمية",
    },
    null,
  );
}

function asBool(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function asStringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/* ══════════════════════════════════════════════════════════════════
   ١) الدرج
   ══════════════════════════════════════════════════════════════════ */

export async function listNotifications(opts: {
  tab: NotificationTab;
  page: number;
  perPage: number;
  withCounts?: boolean;
  signal?: AbortSignal;
}): Promise<NotificationsPage> {
  const res = await api.get<NotificationRow[]>("/notifications", {
    query: {
      // «الكل» = من غير فلتر خالص، مش قيمة تالتة في السيرفر
      scope: opts.tab === "all" ? undefined : opts.tab,
      page: opts.page,
      per_page: opts.perPage,
      with_counts: opts.withCounts ? true : undefined,
    },
    ...(opts.signal ? { signal: opts.signal } : {}),
  });

  if (!Array.isArray(res.data)) throw shapeError("/notifications", res.data);

  const raw = res.counts;
  const counts: NotificationCounts | null =
    raw && typeof raw.all === "number"
      ? { all: raw.all, mine: raw.mine ?? 0, team: raw.team ?? 0 }
      : null;

  return { rows: res.data, meta: res.meta ?? null, counts };
}

/** شارة الجرس — غير المقروء في «يخصني» بس */
export async function unreadCount(signal?: AbortSignal): Promise<number> {
  const res = await api.get<{ unread?: number }>("/notifications/unread-count", {
    ...(signal ? { signal } : {}),
  });
  const value = res.data?.unread;
  if (typeof value !== "number") throw shapeError("/notifications/unread-count", res.data);
  return value;
}

/** تعليم إشعار واحد مقروء. ⛔ ده ما بيغيّرش حالة أي مهمة أبدًا. */
export async function markRead(notificationId: string): Promise<NotificationRow> {
  const res = await api.post<NotificationRow>(
    `/notifications/${encodeURIComponent(notificationId)}/read`,
  );
  if (!res.data?.id) throw shapeError("/notifications/{id}/read", res.data);
  return res.data;
}

/**
 * «تعليم الكل كمقروء» — بيشتغل على الشريحة المفتوحة بس.
 * السيرفر ما بيقبلش «الكل»، فشريحة «الكل» بتتعمل على الاتنين ورا بعض.
 */
export async function markAllRead(tab: NotificationTab): Promise<number> {
  const scopes: NotificationScope[] = tab === "all" ? ["mine", "team"] : [tab];
  let marked = 0;
  for (const scope of scopes) {
    const res = await api.post<{ marked?: number }>("/notifications/read-all", undefined, {
      query: { scope },
    });
    const value = res.data?.marked;
    if (typeof value !== "number") throw shapeError("/notifications/read-all", res.data);
    marked += value;
  }
  return marked;
}

/* ══════════════════════════════════════════════════════════════════
   ٢) التفضيلات — القناة والوقت بس، عمرها ما بتلمس وجود الإشعار في الدرج
   ══════════════════════════════════════════════════════════════════ */

function readPrefs(path: string, data: unknown): NotificationPrefs {
  if (!data || typeof data !== "object") throw shapeError(path, data);
  const d = data as Record<string, unknown>;
  // ⚠️ `handoff` لازم تكون موجودة فعلًا — جسم فاضي مش «كله مقفول»
  if (typeof d["handoff"] !== "boolean") throw shapeError(path, data);
  return {
    handoff: asBool(d["handoff"], true),
    sla_warning: asBool(d["sla_warning"], true),
    announcements: asBool(d["announcements"], true),
    daily_digest: asBool(d["daily_digest"], true),
    telegram_linked: asBool(d["telegram_linked"], false),
    telegram_muted_until: asStringOrNull(d["telegram_muted_until"]),
    digest_at_local: asStringOrNull(d["digest_at_local"]),
    notice_ar: asStringOrNull(d["notice_ar"]),
  };
}

export async function getPreferences(signal?: AbortSignal): Promise<NotificationPrefs> {
  const res = await api.get<unknown>("/notifications/preferences", {
    ...(signal ? { signal } : {}),
  });
  return readPrefs("/notifications/preferences", res.data);
}

export async function updatePreferences(
  patch: Partial<Record<PrefToggleKey, boolean>>,
): Promise<NotificationPrefs> {
  const res = await api.put<unknown>("/notifications/preferences", patch);
  return readPrefs("/notifications/preferences", res.data);
}

/* ══════════════════════════════════════════════════════════════════
   ٣) ربط تليجرام — كود مؤقت، استخدام واحد
   ══════════════════════════════════════════════════════════════════ */

export async function createTelegramLink(): Promise<TelegramLink> {
  const res = await api.post<Record<string, unknown>>("/notifications/telegram/link");
  const link = asStringOrNull(res.data?.["deep_link"]);
  const code = asStringOrNull(res.data?.["manual_code"]);
  // من غير الرابط والكود الشاشة مالهاش أي معنى — ده فشل مش نجاح ناقص
  if (!link || !code) throw shapeError("/notifications/telegram/link", res.data);
  return { deep_link: link, manual_code: code, expires_at: asStringOrNull(res.data?.["expires_at"]) };
}

export async function unlinkTelegram(): Promise<string> {
  const res = await api.post<Record<string, unknown>>("/notifications/telegram/unlink");
  return (
    asStringOrNull(res.data?.["message_ar"]) ??
    "اتفصل الربط. الإشعارات هتفضل في الدرج زي ما هي"
  );
}

/* ══════════════════════════════════════════════════════════════════
   ٤) الوجهة — الإشعار لازم ينتهي عند مهمة أو سجل بعينه
   ══════════════════════════════════════════════════════════════════ */

/**
 * أسماء الشاشات في الباك إند مش متطابقة مع أسماء ملفات الشاشات هنا.
 * الجدول ده هو الترجمة — ومن غيره الضغط على الإشعار كان هيهبط على
 * «الصفحة دي مش موجودة» وهي موجودة فعلًا.
 */
const SCREEN_ALIASES: Record<string, string> = {
  Main: "Desk",
  StudentRecord: "Students",
  StudentsList: "Students",
  StudentLeaves: "Students",
  TeacherFile: "Teachers",
  Journey: "Journeys",
};

/**
 * مسار الإشعار جوّه الواجهة.
 *
 * ⚠️ البارامترات بتتشال عمدًا: الراوتر الحالي بيقارن المسار كنص كامل، يعني
 *    «/desk?task=9» ما بيطابقش أي شاشة وبيهبط على «مش موجودة». لحد ما
 *    الراوتر يفصل الـpathname عن الباقي، بنوصّل الموظف للشاشة الصح بدل
 *    ما نوقّعه على صفحة خطأ. (متسجّل في `needs_other_unit`.)
 */
export function deepLinkPath(link: DeepLink | null | undefined): string {
  const screen = link?.screen ?? null;
  if (!screen) {
    console.error("[notifications] إشعار بلا وجهة — رجّعنا مكتبي", link);
    return HOME_PATH;
  }
  const name = SCREEN_ALIASES[screen] ?? screen;
  const route = ROUTES.find((r) => r.screen === name);
  if (!route) {
    console.error(`[notifications] شاشة مش معروفة في وجهة الإشعار: ${screen} — رجّعنا مكتبي`);
    return HOME_PATH;
  }
  const params = link?.params ?? null;
  if (params && Object.keys(params).length > 0) {
    console.warn(
      `[notifications] بارامترات الوجهة اتشالت (${Object.keys(params).join(", ")}) —` +
        " الراوتر لسه ما بيدعمش الاستعلام في المسار",
    );
  }
  return route.path;
}
